import { useEffect, useRef, type ReactNode } from 'react';
import { App, Flex, Tag, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { subscribeEnvelope } from '@/shared/realtime/bus';
import { relativeWhen } from '@/shared/domain/relativeWhen';
import { TIER_PRESENT, bumpCell, selectNotice } from './notices';
import type { NoticeCell, NoticeLine } from './notices';

const COUNT_TAG_STYLE = { marginInlineEnd: 0 } as const;
/** 协议事实那几行（原始 Steam 码 / 时间 / 账号数）弱化显示：小号 + 次要色。 */
const SUBTLE_STYLE = { fontSize: 12 } as const;
/** 药丸最多列几个主角——更多就折叠成 +N（明细在通知列表）。 */
const MAX_SUBJECT_PILLS = 3;

/**
 * 浮层正文——一格的**当下内容**。
 *
 * 组装顺序（用户裁定 2026-10-24：标题=事件名，正文=主角+详情）：
 * 单主角是「账号：xxx」标签行；多主角先「N 个账号」行再药丸（≤3 + 折叠
 * 计数——标签行装不下名单）；详情行只在「主角唯一」或「全体一致」时印；
 * 末行右对齐相对时间（补弹的旧事不伪装成「刚刚」——时刻来自信封 `ts`，
 * 补弹合成信封带行 `created_at`）。
 */
function describeCell(
  cell: NoticeCell,
  lines: NoticeLine[],
  at: string | null,
  t: ReturnType<typeof useTranslation>['t'],
): ReactNode {
  const subjects = [...cell.subjects.keys()];
  const multi = subjects.length > 1;
  const shownLines = multi ? cell.uniformLines : lines;
  const overflow = subjects.length - MAX_SUBJECT_PILLS;
  const when = relativeWhen(at, t);
  const parts: ReactNode[] = [];
  if (subjects.length === 1) {
    // 单主角：带标签的文本行「账号：xxx」（用户裁定 2026-10-24）——与
    // 「订单：xxx」同形；药丸只留给多主角的风暴格（标签行装不下名单）。
    parts.push(
      <Typography.Text key="who" type="secondary">
        {t('notifications.subjectAccount', { name: subjects[0] })}
      </Typography.Text>,
    );
  }
  if (multi) {
    parts.push(
      <Typography.Text key="count" type="secondary" style={SUBTLE_STYLE}>
        {t('notifications.subjectsCount', { count: subjects.length })}
      </Typography.Text>,
    );
    parts.push(
      <Flex key="who" wrap gap={4}>
        {subjects.slice(0, MAX_SUBJECT_PILLS).map((name) => (
          // 账号名是运行时文本（协议事实），不翻译；人形图标让药丸一眼读作「账号」。
          <Tag key={name} color="blue" bordered={false} icon={<UserOutlined />}>
            {name}
          </Tag>
        ))}
        {overflow > 0 ? <Tag bordered={false}>{`+${overflow}`}</Tag> : null}
      </Flex>,
    );
  }
  for (const [index, line] of (shownLines ?? []).entries()) {
    parts.push(
      <Typography.Text
        key={`line-${index}`}
        type="secondary"
        // ⚠️ `exactOptionalPropertyTypes` ⇒ `style={undefined}` 不合法
        // ⇒ 用条件展开（不给就不给这个属性）。
        {...(line.subtle === true ? { style: SUBTLE_STYLE } : {})}
      >
        {'key' in line ? t(line.key, line.args ?? {}) : line.text}
      </Typography.Text>,
    );
  }
  if (when !== null) {
    parts.push(
      <Flex key="when" justify="flex-end">
        <Typography.Text type="secondary" style={SUBTLE_STYLE}>
          {when}
        </Typography.Text>
      </Flex>,
    );
  }
  if (parts.length === 0) return null;
  return (
    <Flex vertical gap={2}>
      {parts}
    </Flex>
  );
}

export function SseToasts() {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const cellsRef = useRef(new Map<string, NoticeCell>());
  useEffect(
    () =>
      subscribeEnvelope((envelope) => {
        const notice = selectNotice(envelope.type, envelope);
        if (notice === null) return;
        const present = TIER_PRESENT[notice.tier];
        const cell = bumpCell(
          cellsRef.current,
          notice.key,
          notice.subject,
          notice.lines,
          Date.now(),
        );
        /**
         * 走**带类型的** notification 方法（`warning` / `error` / `success` /
         * `info`），而不是 `notification.open`——只有带类型的那几个才会带上 antd
         * 的语义配色（`colorWarning` / `colorError` / …）⇒ 跟主题走，明暗两套
         * 都对。图标交给 antd 的**默认类型图标**（不传 `icon` 即有）——别再在
         * 标题里自放一枚（antd v6 不会替换它 ⇒ 一卡两枚同义图标）。
         */
        const show = notification[present.tone];
        show({
          key: notice.key,
          message: (
            <Flex align="center" gap={8}>
              {/* 标题是**事件名**（帧 + 分档派生，无插值）。 */}
              <span>{t(notice.titleKey)}</span>
              {cell.total > 1 ? (
                <Tag bordered={false} style={COUNT_TAG_STYLE}>
                  {cell.total}
                </Tag>
              ) : null}
            </Flex>
          ),
          // 正文 = **主角 + 详情**；一行都没有 ⇒ 不给正文（空正文框是噪声）。
          description: describeCell(cell, notice.lines, notice.at, t),
          duration: present.duration,
          // 左下角：用户裁定（2026-10-23）——通知从**左下角**升起，不遮右上角的
          // 铃铛与顶栏动作区。全局默认也设成了 bottomLeft（app/providers.tsx），
          // 这里再写一遍是**显式**：这批浮层的位置不该被全局默认悄悄改掉。
          placement: 'bottomLeft',
        });
      }),
    [notification, t],
  );
  return null;
}
